import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Check, Xmark, ArrowLeft } from "iconoir-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fetchBillingConfig, createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

const COL_KEYS = ["free", "plus", "premium"];

export default function Pricing() {
    const nav = useNavigate();
    const { t } = useT();
    const { user } = useAuth();
    const [billingEnabled, setBillingEnabled] = useState(false);
    const [checkoutBusy, setCheckoutBusy] = useState(false);

    useEffect(() => {
        fetchBillingConfig().then((c) => setBillingEnabled(!!c?.enabled)).catch(() => {});
    }, []);

    const handleClickPlus = async () => {
        // If not signed in, send to login first; after sign-in they'll land on /app
        if (!user) {
            nav("/login?next=/pricing");
            return;
        }
        if (!billingEnabled) {
            // Fallback: keep behaviour-preserving — go to app
            nav("/app");
            return;
        }
        setCheckoutBusy(true);
        try {
            const { url } = await createCheckoutSession("/app");
            window.location.assign(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("pricing.checkout_failed"));
            setCheckoutBusy(false);
        }
    };

    const onCtaClick = (key) => {
        if (key === "plus") return handleClickPlus();
        // free + premium just send to app
        nav("/app");
    };

    const tiers = [
        {
            key: "free",
            name: t("pricing.tier.free.name"),
            price: t("pricing.tier.free.price"),
            tag: t("pricing.tier.free.tag"),
            tone: "bg-white",
            cta: t("pricing.cta_continue_free"),
        },
        {
            key: "plus",
            name: t("pricing.tier.plus.name"),
            price: t("pricing.tier.plus.price"),
            tag: t("pricing.tier.plus.tag"),
            tone: "bg-[#e8f1e4]",
            highlight: true,
            cta: t("pricing.cta_try_plus"),
        },
        {
            key: "premium",
            name: t("pricing.tier.premium.name"),
            price: t("pricing.tier.premium.price"),
            tag: t("pricing.tier.premium.tag"),
            tone: "bg-[#ece4f5]",
            cta: t("pricing.cta_join_waitlist"),
        },
    ];

    const features = [
        { group: t("pricing.group.core"), items: [
            { label: t("pricing.feat.chores"), values: [true, true, true] },
            { label: t("pricing.feat.grocery_manual"), values: [true, true, true] },
            { label: t("pricing.feat.meal_basic"), values: [true, true, true] },
            { label: t("pricing.feat.calendar_chat"), values: [true, true, true] },
            { label: t("pricing.feat.households_1"), values: [true, "+", "+"] },
        ]},
        { group: t("pricing.group.smart"), items: [
            { label: t("pricing.feat.ai_meals"), values: [false, true, true] },
            { label: t("pricing.feat.auto_grocery"), values: [false, true, true] },
            { label: t("pricing.feat.voice_actions"), values: [false, true, true] },
            { label: t("pricing.feat.smart_reminders"), values: [false, true, true] },
            { label: t("pricing.feat.ai_quota"), values: [t("pricing.val.basic"), t("pricing.val.high"), t("pricing.val.unlimited")] },
        ]},
        { group: t("pricing.group.premium_features"), items: [
            { label: t("pricing.feat.instacart"), values: [false, false, true] },
            { label: t("pricing.feat.deals"), values: [false, false, true] },
            { label: t("pricing.feat.budget"), values: [false, false, true] },
            { label: t("pricing.feat.insights"), values: [false, false, true] },
        ]},
    ];

    const renderCell = (v) => {
        if (v === true) return <Check className="w-4 h-4 text-[#2F6B43] mx-auto" strokeWidth={3} />;
        if (v === false) return <Xmark className="w-4 h-4 text-[#0d2a1b]/20 mx-auto" />;
        if (v === "+") return <span className="text-xs font-bold text-[#2F6B43]">+</span>;
        return <span className="text-xs font-semibold text-[#0d2a1b]">{v}</span>;
    };

    return (
        <div className="min-h-screen bg-[#faf5ea] py-10 px-4">
            <div className="max-w-5xl mx-auto">
                <button
                    data-testid="pricing-back-btn"
                    onClick={() => nav(-1)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#5b6d63] hover:text-[#0d2a1b] mb-6"
                >
                    <ArrowLeft className="w-4 h-4" /> {t("common.back")}
                </button>

                <div className="text-center mb-10">
                    <div className="text-xs font-bold text-[#2F6B43] uppercase tracking-widest mb-2">{t("pricing.eyebrow")}</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        {t("pricing.headline")}
                    </h1>
                    <p className="mt-3 text-base text-[#5b6d63] max-w-xl mx-auto">
                        {t("pricing.subline")}
                    </p>
                </div>

                {/* Tier cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                    {tiers.map((t2) => {
                        const isCurrent = user && (user.plan || "free") === t2.key;
                        return (
                        <div
                            key={t2.key}
                            data-testid={`tier-card-${t2.key}`}
                            className={`brut-card p-6 flex flex-col ${t2.tone} ${isCurrent ? "ring-2 ring-[#0d2a1b]" : t2.highlight ? "ring-2 ring-[#2F6B43]" : ""}`}
                        >
                            {isCurrent ? (
                                <div data-testid={`tier-current-${t2.key}`} className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0d2a1b] text-white px-2 py-0.5 rounded-full mb-3 self-start">
                                    {t("pricing.current_plan")}
                                </div>
                            ) : t2.highlight ? (
                                <div className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#2F6B43] text-white px-2 py-0.5 rounded-full mb-3 self-start">
                                    {t("pricing.most_popular")}
                                </div>
                            ) : null}
                            <div className="font-display text-2xl font-bold tracking-tight">{t2.name}</div>
                            <div className="text-xs text-[#5b6d63] font-semibold mt-1 uppercase tracking-widest">{t2.tag}</div>
                            <div className="font-display text-4xl font-bold mt-4">{t2.price}</div>
                            <div className="text-xs text-[#5b6d63] mt-1">{t("pricing.per_month")}</div>
                            <button
                                data-testid={`tier-cta-${t2.key}`}
                                onClick={() => onCtaClick(t2.key)}
                                disabled={isCurrent || (checkoutBusy && t2.key === "plus")}
                                className={`mt-6 text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-default ${
                                    t2.highlight
                                        ? "bg-[#2F6B43] text-white hover:bg-[#255636]"
                                        : "bg-white border border-[#0d2a1b]/10 hover:bg-[#faf5ea]"
                                }`}
                            >
                                {isCurrent
                                    ? t("pricing.cta_active")
                                    : (checkoutBusy && t2.key === "plus" ? t("pricing.checking_out") : t2.cta)}
                            </button>
                        </div>
                        );
                    })}
                </div>

                {/* Feature comparison table */}
                <div className="brut-card p-5 sm:p-6 overflow-x-auto">
                    <h2 className="font-display text-2xl font-bold mb-5">{t("pricing.compare_title")}</h2>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b-2 border-[#0d2a1b]/10">
                                <th className="text-left py-2 pr-4 text-xs uppercase tracking-widest font-bold text-[#5b6d63]">{t("pricing.col_feature")}</th>
                                {COL_KEYS.map((k) => (
                                    <th key={k} className="text-center py-2 px-2 text-xs uppercase tracking-widest font-bold text-[#5b6d63] min-w-[90px]">
                                        {t(`pricing.tier.${k}.name`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {features.map((g, gi) => (
                                <React.Fragment key={`g-${gi}`}>
                                    <tr>
                                        <td colSpan={4} className="pt-5 pb-2 text-[11px] font-bold text-[#2F6B43] uppercase tracking-widest">
                                            {g.group}
                                        </td>
                                    </tr>
                                    {g.items.map((row, ri) => (
                                        <tr key={`r-${gi}-${ri}`} className="border-b border-[#0d2a1b]/5">
                                            <td className="py-2.5 pr-4 text-[#0d2a1b]">{row.label}</td>
                                            {row.values.map((v, vi) => (
                                                <td key={`r-${gi}-${ri}-c${vi}`} className="text-center py-2.5 px-2">{renderCell(v)}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="text-center mt-8 text-xs text-[#5b6d63]">
                    {t("pricing.footer")}
                </div>
            </div>
        </div>
    );
}
